import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Row,
  Cell,
  Sortable,
  Checkbox,
  Textfield,
  Button,
  Modal,
  Notification,
} from '@workboard/wobo-ui';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import {
  BulkSettingsTeamResponse,
  BulkSettingsTeam,
  BulkSettingsTeamRow,
} from '../../../../types/teams-management';

const BulkActionContainer = styled('div')`
  height: 50px;
  position: relative;
  background: #eef8fd;

  h3 {
    position: absolute;
    top: -5px;
    left: 5px;
    font-size: 16px;
  }
`;
const BulkActionButtons = styled('div')`
  position: absolute;
  height: 40px;
  top: 10px;
  right: 10px;
`;
const FilterContainer = styled('div')`
  height: 50px;
  width: 100%;
  position: relative;

  h3 {
    position: absolute;
    top: -5px;
    left: 5px;
    font-size: 16px;
  }
`;
const FilterDiv = styled('div')`
  position: absolute;
  height: 40px;
  top: 0px;
  right: 0px;
`;
const NoRequestsContainer = styled('div')`
  width: 350px;
  height: 200px;
  margin: auto;
`;
const NoRequestsMessage = styled('span')`
  padding-left: 5px;
`;

const BulkTeamSettingsModalContent = styled('div')`
  display: flex;
  flex-direction: column;
  height: 650px;
  width: 750px;

  th {
    background-color: #ffffff !important;
  }
`;

export type BulkTeamSettingsProps = {
  userId: number;
};

export const BulkTeamSettingsButton = ({
  userId,
}: BulkTeamSettingsProps): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    type: 'success',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [teams, setTeams] = useState([] as BulkSettingsTeamRow[]);
  const [checkedTeams, setCheckedTeams] = useState([] as number[]);
  const [allChecked, setAllChecked] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [columnName, setColumnName] = useState('');
  const [order, setOrder] = useState('asc');
  const [totalCount, setTotalCount] = useState(0);
  const [filterValue, setFilterValue] = useState('');
  const buttonRef = React.useRef<HTMLInputElement>();
  const handleButtonClick = (): void => {
    setIsOpen(true);
  };
  const handleClose = (): void => {
    setIsOpen(false);
    buttonRef.current.focus();
  };
  const handleNotificationClose = (): void =>
    setAlert({ show: false, type: 'success', message: '' });

  const handleCancelBulkAction = (): void => {
    setCheckedTeams([]);
    setAllChecked(false);
  };

  const getTeams = useCallback(() => {
    setIsLoading(true);
    axios({
      method: 'get',
      url: `${window.wbbaseUrl}/teamUserSearch/team`,
      params: {
        perPage,
        page,
        columnName,
        order,
        userId,
        teamType: 'working_group',
        search: filterValue.length > 1 ? filterValue : undefined,
        allOrgTeams: true,
      },
    })
      .then((res) => {
        if (res.data.message.type !== 'error') {
          const rawTeams = res.data.data;
          const mappedTeams = rawTeams.map(
            (request: BulkSettingsTeam): BulkSettingsTeamRow => ({
              id: request.id,
              teamName: request.name,
              teamIsVisible: request.visible,
              teamManagerName: request.managerName,
              teamManagerEmail: request.managerEmail,
            }),
          );
          setTeams(mappedTeams);
          setTotalCount(res.data.pagination.totalRecords);
        } else {
          setAlert({
            show: true,
            type: 'error',
            message: t('Oops, something went wrong. Please try again later.'),
          });
        }
      })
      .catch(() => {
        setAlert({
          show: true,
          type: 'error',
          message: t('Oops, something went wrong. Please try again later.'),
        });
      })
      .finally(() => setIsLoading(false));
  }, [perPage, page, columnName, order, userId, filterValue]);

  const handleMemberRequestResponse = useCallback(
    async (teamsToSet: BulkSettingsTeamResponse[]) => {
      const config = {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      };
      axios
        .patch(
          `${window.wbbaseUrl}/teams/changeTeamsVisibility`,
          {
            teams: teamsToSet,
          },
          config,
        )
        .then((res) => {
          if (res.data.message.type !== 'error') {
            setAlert({
              show: true,
              type: 'success',
              message: t(
                checkedTeams.length > 1
                  ? '{{count}} working groups changed to {{status}}'
                  : '{{count}} working group changed to {{status}}',
                {
                  count: checkedTeams.length || 1,
                  status: teamsToSet[0].isVisible ? t('visible') : t('hidden'),
                },
              ),
            });
            setCheckedTeams([]);
            setAllChecked(false);
            getTeams();
          } else {
            setAlert({
              show: true,
              type: 'error',
              message: t('Oops, something went wrong. Please try again later.'),
            });
          }
        })
        .catch(() => {
          setAlert({
            show: true,
            type: 'error',
            message: t('Oops, something went wrong. Please try again later.'),
          });
        })
        .finally(() => setIsLoading(false));
    },
    [checkedTeams.length, getTeams, t],
  );

  useEffect(() => {
    getTeams();
  }, [getTeams, page, perPage]);

  const handleTableChange = (pageData: {
    page: number;
    rowsPerPage: number;
    columnName: string;
    order: string;
  }): void => {
    if (pageData.page + 1 !== page && pageData.page >= 0) {
      setPage(pageData.page + 1);
    }
    if (pageData.rowsPerPage !== perPage) {
      setPerPage(pageData.rowsPerPage);
    }
    if (pageData.columnName !== columnName) {
      setColumnName(pageData.columnName);
    }
    if (pageData.order !== order) {
      setOrder(pageData.order);
    }
  };

  const handleFilterChange = (e: { target: { value: string } }): void => {
    const newFilterValue = e.target.value;
    setFilterValue(newFilterValue);
  };

  const handleCheckAll = (): void => {
    if (allChecked) {
      setCheckedTeams([]);
      setAllChecked(false);
    } else {
      setCheckedTeams(teams.map((request) => request.id));
      setAllChecked(true);
    }
  };

  const HeaderRow = ({ ...props }): JSX.Element => (
    <Row>
      <Cell style={{ background: '#fff' }} align="center">
        <Checkbox onClick={handleCheckAll} isChecked={allChecked} />
      </Cell>
      <Sortable {...props} style={{ background: '#fff' }} align="left">
        {t('Group name')}
      </Sortable>
      <Sortable {...props} style={{ background: '#fff' }} align="left">
        {t('Visibility')}
      </Sortable>
      <Sortable {...props} style={{ background: '#fff' }} align="left">
        {t('Manager')}
      </Sortable>
      <Sortable {...props} style={{ background: '#fff' }} align="left">
        {t('Manager email')}
      </Sortable>
    </Row>
  );

  const TableRow = ({
    id,
    teamManagerName,
    teamIsVisible,
    teamManagerEmail,
    teamName,
    ...props
  }: BulkSettingsTeamRow): JSX.Element => {
    const checked = checkedTeams.includes(id);
    return (
      <Row {...props} style={{ backgroundColor: checked ? '#EEF8FD' : '#FFF' }}>
        <Cell align="center">
          <Checkbox
            onClick={() => {
              if (checked) {
                setCheckedTeams(
                  checkedTeams.filter((requestId) => requestId !== id),
                );
                setAllChecked(false);
              } else {
                setCheckedTeams([...checkedTeams, id]);
                if (checkedTeams.length + 1 === teams.length) {
                  setAllChecked(true);
                }
              }
            }}
            isChecked={checked}
          />
        </Cell>
        <Cell align="left">{teamName}</Cell>
        <Cell align="left">{teamIsVisible ? t('Visible') : t('Hidden')}</Cell>
        <Cell align="left">{teamManagerName}</Cell>
        <Cell align="left">{teamManagerEmail}</Cell>
      </Row>
    );
  };

  const setSelectedTeamsVisible = (): void => {
    const teamsToSetVisible = checkedTeams.map((teamId) => ({
      teamId,
      isVisible: true,
    }));
    handleMemberRequestResponse(teamsToSetVisible);
  };

  const setSelectedTeamsInvisible = (): void => {
    const teamsToSetInvisible = checkedTeams.map((teamId) => ({
      teamId,
      isVisible: false,
    }));
    handleMemberRequestResponse(teamsToSetInvisible);
  };

  return (
    <>
      <Button
        ref={buttonRef}
        kind="normal"
        onClick={handleButtonClick}
        variant="tertiary"
      >
        {t('Bulk change team listing status')}
      </Button>
      <Modal
        isOpen={isOpen}
        dismissLabel={t('Cancel')}
        onDismiss={handleClose}
        onSubmit={handleClose}
        submitLabel={t('Done')}
        title={t('Change team listing status')}
      >
        <BulkTeamSettingsModalContent>
          {checkedTeams.length > 0 ? (
            <div>
              <BulkActionContainer>
                <h3>
                  {t(
                    checkedTeams.length === 1
                      ? '{{count}} working group selected'
                      : '{{count}} working groups selected',
                    { count: checkedTeams.length },
                  )}
                </h3>
                <BulkActionButtons>
                  <Button
                    style={{ marginLeft: '10px' }}
                    onClick={setSelectedTeamsVisible}
                    variant="secondary"
                  >
                    {t('Change to Visible')}
                  </Button>
                  <Button
                    style={{ marginLeft: '10px' }}
                    onClick={setSelectedTeamsInvisible}
                    variant="secondary"
                  >
                    {t('Change to Hidden')}
                  </Button>
                  <Button
                    style={{ marginLeft: '10px' }}
                    onClick={handleCancelBulkAction}
                    variant="ghost"
                  >
                    {t('Cancel')}
                  </Button>
                </BulkActionButtons>
              </BulkActionContainer>
            </div>
          ) : (
            <div>
              <FilterContainer>
                <h3>
                  {t(
                    totalCount === 1
                      ? '{{count}} working group'
                      : '{{count}} working groups',
                    { count: totalCount },
                  )}
                </h3>
                <FilterDiv>
                  <Textfield
                    onChange={handleFilterChange}
                    placeholder={t('Filter')}
                    type="text"
                    value={filterValue}
                  />
                </FilterDiv>
              </FilterContainer>
            </div>
          )}
          {teams.length === 0 ? (
            <NoRequestsContainer>
              <InfoOutlined />
              <NoRequestsMessage>
                {t('There are no teams to edit')}
              </NoRequestsMessage>
            </NoRequestsContainer>
          ) : (
            <Table
              data={teams}
              data-testid="wbui-table-team-member-requests"
              header={HeaderRow}
              row={TableRow}
              rowsPerPage={perPage}
              rowsPerPageValues={{
                isAll: false,
                pageNumbers: [10, 25, 50],
              }}
              totalDataCount={totalCount > perPage ? totalCount : undefined}
              isLoading={isLoading}
              onChange={handleTableChange}
            />
          )}
        </BulkTeamSettingsModalContent>
      </Modal>
      <Notification
        open={alert.show}
        severity={alert.type}
        message={alert.message}
        onClose={handleNotificationClose}
      />
    </>
  );
};

export default BulkTeamSettingsButton;
